export const SWR_CACHE_PREFIX = 'staleWhileRevalidate:'

type CachedEntry = {
  cachedAt: number
}

/**
 * Sums the approximate size (in UTF-16 chars — a fast proxy for the browser's
 * localStorage quota accounting) of every SWR cache entry currently in
 * localStorage. Never throws.
 */
export const getSwrNamespaceBytes = (): number => {
  let total = 0
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key || !key.startsWith(SWR_CACHE_PREFIX)) continue
      const value = localStorage.getItem(key)
      if (value) total += key.length + value.length
    }
  } catch {
    return total
  }
  return total
}

/**
 * Bounds total SWR namespace growth: if the namespace exceeds `maxBytes`, evicts
 * oldest-`cachedAt` entries until it is back under budget. This is the LRU-ish
 * counterpart to the per-entry size cap — without it the namespace can grow
 * unbounded (one entry per query-variables permutation) and starve the shared
 * localStorage quota that session/auth writes depend on. Returns the count of
 * entries evicted. Never throws.
 */
export const evictSwrCacheEntriesToBudget = (maxBytes: number): number => {
  try {
    // Cheap gate: sum entry byte-lengths (no JSON.parse) and bail when already
    // under budget — the overwhelmingly common case. Only when over budget do we
    // pay the parse+sort needed to pick oldest-first victims. Parsing the whole
    // namespace on every write costs tens of ms/page; the length-only sum is ~µs.
    if (getSwrNamespaceBytes() <= maxBytes) return 0

    const swrEntries: { key: string; cachedAt: number; bytes: number }[] = []
    let total = 0

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key || !key.startsWith(SWR_CACHE_PREFIX)) continue

      const value = localStorage.getItem(key) ?? ''
      const bytes = key.length + value.length
      total += bytes

      let cachedAt = 0
      try {
        cachedAt = (JSON.parse(value) as CachedEntry)?.cachedAt ?? 0
      } catch {
        // Unparseable entry — treat as oldest so it gets evicted first
      }

      swrEntries.push({ key, cachedAt, bytes })
    }

    if (total <= maxBytes) return 0

    // Sort oldest first, then evict until under budget
    swrEntries.sort((a, b) => a.cachedAt - b.cachedAt)

    let evicted = 0
    for (const entry of swrEntries) {
      if (total <= maxBytes) break
      localStorage.removeItem(entry.key)
      total -= entry.bytes
      evicted += 1
    }

    return evicted
  } catch {
    return 0
  }
}

type EvictOptions = {
  /** Maximum number of entries to evict. Defaults to half of all SWR entries. */
  maxEntriesToEvict?: number
}

/**
 * Scans localStorage for SWR cache entries (`staleWhileRevalidate:` prefix),
 * sorts by `cachedAt` oldest-first, and removes up to half (or `maxEntriesToEvict`).
 * Returns the count of entries evicted. Never throws.
 */
export const evictSwrCacheEntries = (options?: EvictOptions): number => {
  try {
    const swrEntries: { key: string; cachedAt: number }[] = []

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key || !key.startsWith(SWR_CACHE_PREFIX)) continue

      let cachedAt = 0
      try {
        const parsed = JSON.parse(localStorage.getItem(key)!) as CachedEntry
        cachedAt = parsed?.cachedAt ?? 0
      } catch {
        // Unparseable entry — treat as oldest (cachedAt = 0) so it gets evicted first
      }

      swrEntries.push({ key, cachedAt })
    }

    if (swrEntries.length === 0) return 0

    // Sort oldest first
    swrEntries.sort((a, b) => a.cachedAt - b.cachedAt)

    const limit = options?.maxEntriesToEvict ?? Math.ceil(swrEntries.length / 2)
    const toEvict = swrEntries.slice(0, limit)

    for (const entry of toEvict) {
      localStorage.removeItem(entry.key)
    }

    return toEvict.length
  } catch {
    return 0
  }
}
